/**
 * Análise de uso com a Contentsquare (ex-Hotjar), 14/10/2026: mapas de calor,
 * gravações e o que a pessoa faz nos bloqueios da marca (A167).
 *
 * Desligada até existir `VITE_CONTENTSQUARE_TAG_ID` (no `.env.production`, que
 * não é versionado). Mesmo com o ID, só carrega:
 *
 * - **com o consentimento da pessoa** (`AvisoDeAnalise`), guardado neste
 *   aparelho — é cookie de análise, e o app tem dado de saúde (LGPD);
 * - **nas telas da paciente e do cadastro** (`/eu`, `/cadastro`): os painéis
 *   da marca e do mantenedor ficam de fora.
 *
 * Privacidade por padrão: todo o texto da página vai mascarado
 * (`setPIISelectors` no `body`), e os atributos que podem trazer nome ou
 * endereço de imagem também. Clique, rolagem e mapa de calor não dependem do
 * texto. Um trecho seguro que precise aparecer nas gravações ganha
 * `data-cs-capture`. Das URLs vai só o caminho: a query pode ter nome de
 * pessoa (`?participantName=`).
 *
 * Referência: https://docs.contentsquare.com/en/web/ e
 * https://docs.contentsquare.com/en/web/personal-data-handling/
 */
/** O pedaço do roteador do React Router que a análise usa. */
interface Roteador {
  state: { location: { pathname: string } };
  subscribe: (fn: (estado: { location: { pathname: string } }) => void) => () => void;
}

type Comando = unknown[];
declare global {
  interface Window {
    _uxa?: Comando[];
  }
}

const TAG_ID = (import.meta.env.VITE_CONTENTSQUARE_TAG_ID || '').trim();
const CHAVE_DO_CONSENTIMENTO = 'eva_analise_consentimento';
const ROTAS_ANALISADAS = ['/eu', '/cadastro'];

export type Consentimento = 'sim' | 'nao' | null;

/** Há tag configurada neste build? Sem ela, nada de análise nem de aviso. */
export const analiseDisponivel = TAG_ID.length > 0;

export function rotaAnalisada(caminho: string): boolean {
  return ROTAS_ANALISADAS.some((r) => caminho === r || caminho.startsWith(`${r}/`));
}

export function lerConsentimento(): Consentimento {
  try {
    const v = localStorage.getItem(CHAVE_DO_CONSENTIMENTO);
    return v === 'sim' || v === 'nao' ? v : null;
  } catch {
    return null;
  }
}

function fila(): Comando[] {
  window._uxa = window._uxa || [];
  return window._uxa;
}

let roteador: Roteador | null = null;
let carregada = false;
let ultimoCaminho: string | null = null;
// O último contexto informado: vai junto quando a tag carrega (ex.: a pessoa aceitou no meio da visita).
let contexto: { marca?: string | null; jornada?: string | null; papel?: string | null } = {};

function enviarContexto() {
  const uxa = fila();
  if (contexto.marca) uxa.push(['setCustomVariable', 1, 'Marca', contexto.marca, 3]);
  if (contexto.jornada) uxa.push(['setCustomVariable', 2, 'Jornada', contexto.jornada, 3]);
  if (contexto.papel) uxa.push(['setCustomVariable', 3, 'Papel', contexto.papel, 3]);
}

/** Carrega a tag (uma vez), com o mascaramento antes dela, como a documentação pede. */
function carregar(caminho: string) {
  if (carregada) return;
  carregada = true;
  const uxa = fila();
  uxa.push([
    'setPIISelectors',
    {
      PIISelectors: ['body'],
      Attributes: [{ selector: '*', attrName: ['aria-label', 'title', 'alt', 'placeholder', 'value', 'src', 'href'] }],
    },
  ]);
  enviarContexto();
  uxa.push(['setPath', caminho]);
  ultimoCaminho = caminho;
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `https://t.contentsquare.net/uxa/${encodeURIComponent(TAG_ID)}.js`;
  document.head.appendChild(script);
}

/** Uma tela nova: carrega na primeira tela analisada; depois, uma pageview por troca. */
function aoNavegar(caminho: string) {
  if (!analiseDisponivel || lerConsentimento() !== 'sim' || !rotaAnalisada(caminho)) return;
  if (!carregada) return carregar(caminho);
  if (caminho === ultimoCaminho) return;
  ultimoCaminho = caminho;
  fila().push(['trackPageview', caminho]);
}

/** Liga a análise ao roteador. Chamado uma vez, na entrada do app. */
export function iniciarAnalise(r: Roteador) {
  if (!analiseDisponivel || roteador) return;
  roteador = r;
  aoNavegar(r.state.location.pathname);
  r.subscribe((estado) => aoNavegar(estado.location.pathname));
}

/** A decisão da pessoa no `AvisoDeAnalise`. Aceitar já liga a análise nesta tela. */
export function consentir(sim: boolean) {
  try {
    localStorage.setItem(CHAVE_DO_CONSENTIMENTO, sim ? 'sim' : 'nao');
  } catch {
    /* sem armazenamento: o aviso volta na próxima visita */
  }
  if (sim && roteador) aoNavegar(roteador.state.location.pathname);
}

/** Marca, jornada e papel da visita, para filtrar mapas e gravações (escopo de visita). */
export function definirContexto(ctx: { marca?: string | null; jornada?: string | null; papel?: string | null }) {
  if (!analiseDisponivel) return;
  contexto = ctx;
  if (carregada) enviarContexto();
}

/** Um momento da sessão (ex.: o aviso de bloqueio que ela viu), para filtrar gravações. */
export function registrarMomento(chave: string, valor: string) {
  if (!analiseDisponivel || lerConsentimento() !== 'sim' || !carregada) return;
  fila().push(['trackDynamicVariable', { key: chave, value: valor }]);
}
